// ============================================================
// FILE: src/context/AuthContext.jsx
// PURPOSE: Stores logged-in user globally so every page can
//          access it. Uses React Context API.
// ============================================================

import React, { createContext, useContext, useState, useEffect } from 'react';

// 1. Create the context
const AuthContext = createContext(null);

// 2. Provider wraps the whole app (in App.jsx)
export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [token,   setToken]   = useState(null);
  const [loading, setLoading] = useState(true);

  // On app start: restore session from localStorage
  useEffect(() => {
    const savedToken = localStorage.getItem('ec_token');
    const savedUser  = localStorage.getItem('ec_user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  // Called after successful login
  // IMPORTANT: returns the redirect path based on role
  const login = (userData, jwtToken) => {
    setUser(userData);
    setToken(jwtToken);
    localStorage.setItem('ec_token', jwtToken);
    localStorage.setItem('ec_user', JSON.stringify(userData));

    // Return redirect path — Client goes to client portal, everyone else to /
    const role = userData.role || userData.Role;
    return role === 'Client' ? '/client/dashboard' : '/';
  };

  // Called on logout
  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('ec_token');
    localStorage.removeItem('ec_user');
  };

  // Check if current user has one of the given roles
  const hasRole = (...roles) => {
    if (!user) return false;
    const role = user.role || user.Role;
    return roles.includes(role);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
};

// 3. Custom hook — use this in any component: const { user } = useAuth();
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};